'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { PackageSearch, ClipboardList, Scan, Boxes, TruckIcon, BarChart3, Settings, ChevronLeft, ChevronRight, User, Bell, AlertTriangle, Package, MapPin, ChevronDown, ShieldCheck, PlusCircle, ScrollText, GitBranch, FileText, Package2, Box, Layers, Tag, SlidersHorizontal, Building2, ShoppingCart } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  badge?: number;
  badgeVariant?: 'warning' | 'error' | 'info';
  group: string;
  children?: { id: string; label: string; href: string; icon: React.ComponentType<{ size?: number; className?: string }> }[];
}

const navItems: NavItem[] = [
  { id: 'nav-inbound', label: 'Inbound / GRN', href: '/inbound-grn', icon: PackageSearch, badge: 7, badgeVariant: 'info', group: 'Operations' },
  { id: 'nav-rack-slotting', label: 'Rack Slotting', href: '/rack-slotting', icon: MapPin, group: 'Operations' },
  {
    id: 'nav-inventory', label: 'Inventory', href: '#', icon: Boxes, group: 'Operations',
    children: [
      { id: 'nav-inventory-add', label: 'Item Inventory', href: '/inventory/add-items', icon: PlusCircle },
      { id: 'nav-inventory-qc', label: 'QC Review', href: '/inventory/qc-review', icon: ShieldCheck },
      { id: 'nav-kit-inventory', label: 'Kit Inventory', href: '/inventory/kit-inventory', icon: Layers },
    ],
  },
  { id: 'nav-picking', label: 'Picking', href: '/picking', icon: ClipboardList, badge: 2, badgeVariant: 'warning', group: 'Operations' },
  { id: 'nav-kitting', label: 'Kitting', href: '/', icon: Scan, badge: 3, badgeVariant: 'warning', group: 'Operations' },
  { id: 'nav-requisition', label: 'Requisition', href: '/requisition', icon: ClipboardList, group: 'Operations' },
  { id: 'nav-order-management', label: 'Order Management', href: '/order-management', icon: ShoppingCart, group: 'Operations' },
  { id: 'nav-dispatch', label: 'Dispatch', href: '/dispatch', icon: TruckIcon, group: 'Operations' },
  { id: 'nav-audit-trail', label: 'Audit Trail', href: '/audit-trail', icon: ScrollText, group: 'Operations' },
  { id: 'nav-kit-traceability', label: 'Kit Traceability', href: '/kit-traceability', icon: GitBranch, group: 'Operations' },
  { id: 'nav-compliance-reports', label: 'Compliance Reports', href: '/compliance-reports', icon: FileText, group: 'Operations' },
  { id: 'nav-analytics', label: 'Analytics', href: '/analytics-dashboard', icon: BarChart3, group: 'Operations' },
  {
    id: 'nav-master', label: 'Master', href: '#', icon: Settings, group: 'Master Data',
    children: [
      { id: 'nav-item-master', label: 'Item Master', href: '/item-master', icon: Package },
      { id: 'nav-kit-master', label: 'Kit Master', href: '/kit-master', icon: Package2 },
      { id: 'nav-box-master', label: 'Box Master', href: '/box-master', icon: Box },
      { id: 'nav-cube-master', label: 'Cube Master', href: '/cube-master', icon: Layers },
      { id: 'nav-version', label: 'Version', href: '/version', icon: Tag },
      { id: 'nav-attributes', label: 'Attributes', href: '/attributes', icon: SlidersHorizontal },
      { id: 'nav-locations', label: 'Locations', href: '/locations', icon: MapPin },
      { id: 'nav-company', label: 'Company', href: '/company', icon: Building2 },
    ],
  },
  { id: 'nav-settings', label: 'Settings', href: '#', icon: Settings, group: 'System' },
];

const groups = ['Operations', 'Master Data', 'System'];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set(['nav-inventory']));
  const pathname = usePathname();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  const isGroupActive = (item: NavItem) => {
    if (isActive(item.href)) return true;
    return item.children?.some((c) => isActive(c.href)) ?? false;
  };

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <aside
      className={`sidebar-transition flex flex-col bg-card border-r border-border h-screen sticky top-0 z-40 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center h-16 border-b border-border px-3 ${collapsed ? 'justify-center' : 'gap-2.5'}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-bold text-base tracking-tight text-foreground truncate">
            WMS_Pro
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 scrollbar-thin">
        {groups.map((group) => {
          const items = navItems.filter((n) => n.group === group);
          return (
            <div key={`group-${group}`} className="mb-4">
              {!collapsed && (
                <p className="px-4 mb-1 text-2xs font-semibold tracking-widest uppercase text-muted-foreground">
                  {group}
                </p>
              )}
              {collapsed && <div className="mx-3 my-1 h-px bg-border" />}
              <ul>
                {items.map((item) => {
                  const Icon = item.icon;
                  const active = isGroupActive(item);
                  const isExpanded = expandedItems.has(item.id);
                  const hasChildren = item.children && item.children.length > 0;

                  return (
                    <li key={item.id}>
                      {hasChildren ? (
                        <button
                          type="button"
                          onClick={() => !collapsed && toggleExpand(item.id)}
                          className={`relative w-full flex items-center gap-3 mx-2 px-2.5 py-2 rounded-lg text-sm font-medium transition-all duration-150 group ${
                            active
                              ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                          }`}
                          style={{ width: 'calc(100% - 1rem)' }}
                          title={collapsed ? item.label : undefined}
                        >
                          <Icon size={18} className="flex-shrink-0" />
                          {!collapsed && (
                            <>
                              <span className="flex-1 truncate text-left">{item.label}</span>
                              {item.badge !== undefined && (
                                <span
                                  className={`text-2xs font-bold px-1.5 py-0.5 rounded-full ${
                                    item.badgeVariant === 'error' ? 'bg-red-500/20 text-red-400'
                                      : item.badgeVariant === 'warning'? 'bg-accent/20 text-accent' :'bg-primary/20 text-primary'
                                  }`}
                                >
                                  {item.badge}
                                </span>
                              )}
                              <ChevronDown
                                size={13}
                                className={`text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                              />
                            </>
                          )}
                          {collapsed && item.badge !== undefined && (
                            <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-accent" />
                          )}
                          {collapsed && (
                            <div className="absolute left-full ml-2 px-2 py-1 bg-card border border-border rounded text-xs text-foreground whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">
                              {item.label}
                            </div>
                          )}
                        </button>
                      ) : (
                        <Link
                          href={item.href}
                          className={`relative flex items-center gap-3 mx-2 px-2.5 py-2 rounded-lg text-sm font-medium transition-all duration-150 group ${
                            active
                              ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                          }`}
                          title={collapsed ? item.label : undefined}
                        >
                          <Icon size={18} className="flex-shrink-0" />
                          {!collapsed && (
                            <>
                              <span className="flex-1 truncate">{item.label}</span>
                              {item.badge !== undefined && (
                                <span
                                  className={`text-2xs font-bold px-1.5 py-0.5 rounded-full ${
                                    item.badgeVariant === 'error' ? 'bg-red-500/20 text-red-400'
                                      : item.badgeVariant === 'warning'? 'bg-accent/20 text-accent' :'bg-primary/20 text-primary'
                                  }`}
                                >
                                  {item.badge}
                                </span>
                              )}
                            </>
                          )}
                          {collapsed && item.badge !== undefined && (
                            <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-accent" />
                          )}
                          {collapsed && (
                            <div className="absolute left-full ml-2 px-2 py-1 bg-card border border-border rounded text-xs text-foreground whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">
                              {item.label}
                              {item.badge !== undefined && (
                                <span className="ml-1.5 text-accent font-bold">{item.badge}</span>
                              )}
                            </div>
                          )}
                        </Link>
                      )}

                      {/* Sub-items */}
                      {hasChildren && isExpanded && !collapsed && (
                        <ul className="mt-0.5 ml-4 border-l border-border pl-2 space-y-0.5">
                          {item.children!.map((child) => {
                            const ChildIcon = child.icon;
                            const childActive = isActive(child.href);
                            return (
                              <li key={child.id}>
                                <Link
                                  href={child.href}
                                  className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 ${
                                    childActive
                                      ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                                  }`}
                                >
                                  <ChildIcon size={14} className="flex-shrink-0" />
                                  <span className="truncate">{child.label}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* Alerts indicator */}
      {!collapsed && (
        <div className="mx-3 mb-2 px-3 py-2 rounded-lg bg-accent/10 border border-accent/20 flex items-center gap-2">
          <AlertTriangle size={14} className="text-accent flex-shrink-0" />
          <span className="text-xs text-accent font-medium">4 items expiring in 30d</span>
        </div>
      )}

      {/* User + collapse */}
      <div className="border-t border-border p-3 space-y-2">
        {!collapsed && (
          <div className="flex items-center gap-2.5 px-1">
            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <User size={14} className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-foreground truncate">Prasun Sharma</p>
              <p className="text-2xs text-muted-foreground truncate">Admin · HH-01</p>
            </div>
            <Bell size={14} className="text-muted-foreground flex-shrink-0" />
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 px-2 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
        >
          {collapsed ? <ChevronRight size={14} /> : (
            <>
              <ChevronLeft size={14} />
              <span>Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}